import type {
  Banner,
  CheckoutPayload,
  CustomerSummary,
  DashboardSummary,
  Expense,
  FinanceSummary,
  Giftcard,
  ManualOrderPayload,
  Order,
  Product,
  Promotion,
  ReportSummary,
  StoreSender,
  StoreSettings
} from '../types';

async function parseResponse<T>(response: Response): Promise<T> {
  const text = await response.text();
  const body = text ? JSON.parse(text) : null;
  if (!response.ok) {
    throw new Error(body?.error || body?.message || 'No se pudo completar la accion');
  }
  return body as T;
}

function adminHeaders() {
  const token = window.localStorage.getItem('runmore_admin_token') || '';
  return {
    'content-type': 'application/json',
    authorization: token ? `Bearer ${token}` : ''
  };
}

function authHeaderOnly() {
  const token = window.localStorage.getItem('runmore_admin_token') || '';
  return { authorization: token ? `Bearer ${token}` : '' };
}

export const api = {
  async products(): Promise<Product[]> {
    const response = await fetch('/api/products');
    const body = await parseResponse<{ products: Product[] }>(response);
    return body.products;
  },

  async product(slug: string): Promise<Product> {
    const response = await fetch(`/api/products/${slug}`);
    const body = await parseResponse<{ product: Product }>(response);
    return body.product;
  },

  async banners(): Promise<Banner[]> {
    const response = await fetch('/api/banners');
    const body = await parseResponse<{ banners: Banner[] }>(response);
    return body.banners;
  },

  async checkout(payload: CheckoutPayload): Promise<{ orderId: string; checkoutUrl: string; mock: boolean }> {
    const response = await fetch('/api/checkout', {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify(payload)
    });
    return parseResponse(response);
  },

  async checkCoupon(code: string): Promise<{ valid: boolean; code: string; type?: string; discountType?: string; discountValue?: number; minOrder?: number; description?: string; message?: string }> {
    const response = await fetch(`/api/coupon/${encodeURIComponent(code)}`);
    return parseResponse(response);
  },

  async newsletter(email: string): Promise<{ ok: boolean }> {
    const response = await fetch('/api/newsletter', {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ email })
    });
    return parseResponse(response);
  },

  async checkGiftcard(code: string): Promise<{ valid: boolean; code?: string; balance?: number; recipientName?: string; message?: string }> {
    const response = await fetch(`/api/giftcards/check/${encodeURIComponent(code)}`);
    return parseResponse(response);
  },

  async siteConfig(): Promise<{
    ga4Id: string; gtmId: string; metaPixelId: string; tiktokPixelId: string; hotjarId: string;
    googleSiteVerification: string; whatsappNumber: string; instagramUrl: string; facebookUrl: string;
  }> {
    const response = await fetch('/api/site-config');
    return parseResponse(response);
  },

  customer: {
    async login(email: string, orderNumber: string): Promise<{ token: string; email: string }> {
      const response = await fetch('/api/me/login', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ email, orderNumber })
      });
      return parseResponse(response);
    },
    headers() {
      const token = window.localStorage.getItem('runmore_customer_token') || '';
      return { authorization: token ? `Bearer ${token}` : '', 'content-type': 'application/json' };
    },
    async orders(): Promise<Order[]> {
      const response = await fetch('/api/me/orders', { headers: api.customer.headers() });
      const body = await parseResponse<{ orders: Order[] }>(response);
      return body.orders;
    },
    async giftcards(): Promise<Giftcard[]> {
      const response = await fetch('/api/me/giftcards', { headers: api.customer.headers() });
      const body = await parseResponse<{ giftcards: Giftcard[] }>(response);
      return body.giftcards;
    },
    async profile(): Promise<{ email: string; profile: Record<string, string> }> {
      const response = await fetch('/api/me/profile', { headers: api.customer.headers() });
      return parseResponse(response);
    }
  },

  async trackOrder(orderNumber: string, email: string): Promise<{
    order: {
      orderNumber: number;
      status: string;
      createdAt: string;
      updatedAt: string;
      total: number;
      shippingAddress: string;
      shippingCity: string;
      trackingNumber: string;
      trackingProvider: string;
      items: Array<{ name: string; sku: string; color: string; size: string; quantity: number; total: number }>;
    };
  }> {
    const response = await fetch(`/api/track?order=${encodeURIComponent(orderNumber)}&email=${encodeURIComponent(email)}`);
    return parseResponse(response);
  },

  admin: {
    async login(email: string, password: string): Promise<{ token: string }> {
      const response = await fetch('/api/admin/login', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ email, password })
      });
      return parseResponse(response);
    },

    async logout(): Promise<void> {
      try {
        await fetch('/api/admin/logout', { method: 'POST', headers: adminHeaders() });
      } catch {
        // ignore network errors on logout — token will be removed locally anyway
      }
    },

    async summary(): Promise<DashboardSummary> {
      const response = await fetch('/api/admin/summary', { headers: adminHeaders() });
      return parseResponse(response);
    },

    async products(): Promise<Product[]> {
      const response = await fetch('/api/admin/products', { headers: adminHeaders() });
      const body = await parseResponse<{ products: Product[] }>(response);
      return body.products;
    },

    async saveProduct(product: Partial<Product>): Promise<Product> {
      const method = product.id ? 'PUT' : 'POST';
      const url = product.id ? `/api/admin/products/${product.id}` : '/api/admin/products';
      const response = await fetch(url, { method, headers: adminHeaders(), body: JSON.stringify(product) });
      const body = await parseResponse<{ product: Product }>(response);
      return body.product;
    },

    async archiveProduct(id: string): Promise<void> {
      const response = await fetch(`/api/admin/products/${id}`, { method: 'DELETE', headers: adminHeaders() });
      await parseResponse(response);
    },

    async bulkPriceUpdate(payload: { mode: 'percent' | 'fixed'; value: number; direction: 'up' | 'down'; category?: string }): Promise<{ affected: number }> {
      const response = await fetch('/api/admin/products/bulk-price', { method: 'POST', headers: adminHeaders(), body: JSON.stringify(payload) });
      return parseResponse(response);
    },

    async exportProducts(): Promise<void> {
      window.open(`/api/admin/products/export`, '_blank');
    },

    async orders(): Promise<Order[]> {
      const response = await fetch('/api/admin/orders', { headers: adminHeaders() });
      const body = await parseResponse<{ orders: Order[] }>(response);
      return body.orders;
    },

    async order(id: string): Promise<Order> {
      const response = await fetch(`/api/admin/orders/${id}`, { headers: adminHeaders() });
      const body = await parseResponse<{ order: Order }>(response);
      return body.order;
    },

    async updateOrder(id: string, patch: { status?: string; trackingNumber?: string; trackingProvider?: string; adminNotes?: string }): Promise<Order> {
      const response = await fetch(`/api/admin/orders/${id}`, { method: 'PATCH', headers: adminHeaders(), body: JSON.stringify(patch) });
      const body = await parseResponse<{ order: Order }>(response);
      return body.order;
    },

    async exportOrders(): Promise<void> {
      window.open(`/api/admin/orders/export`, '_blank');
    },

    async starkenLabel(id: string): Promise<{ trackingNumber: string; labelUrl: string }> {
      const response = await fetch(`/api/admin/orders/${id}/starken-label`, { method: 'POST', headers: adminHeaders() });
      return parseResponse(response);
    },

    async uploadAsset(file: File): Promise<{ url: string; key: string }> {
      const form = new FormData();
      form.append('file', file);
      const response = await fetch('/api/admin/assets', { method: 'POST', headers: authHeaderOnly(), body: form });
      return parseResponse(response);
    },

    async promotions(): Promise<Promotion[]> {
      const response = await fetch('/api/admin/promotions', { headers: adminHeaders() });
      const body = await parseResponse<{ promotions: Promotion[] }>(response);
      return body.promotions;
    },

    async savePromotion(promo: Partial<Promotion>): Promise<Promotion> {
      const method = promo.id ? 'PUT' : 'POST';
      const url = promo.id ? `/api/admin/promotions/${promo.id}` : '/api/admin/promotions';
      const response = await fetch(url, { method, headers: adminHeaders(), body: JSON.stringify(promo) });
      const body = await parseResponse<{ promotion: Promotion }>(response);
      return body.promotion;
    },

    async deletePromotion(id: string): Promise<void> {
      const response = await fetch(`/api/admin/promotions/${id}`, { method: 'DELETE', headers: adminHeaders() });
      await parseResponse(response);
    },

    async banners(): Promise<Banner[]> {
      const response = await fetch('/api/admin/banners', { headers: adminHeaders() });
      const body = await parseResponse<{ banners: Banner[] }>(response);
      return body.banners;
    },

    async saveBanner(banner: Partial<Banner>): Promise<Banner> {
      const method = banner.id ? 'PUT' : 'POST';
      const url = banner.id ? `/api/admin/banners/${banner.id}` : '/api/admin/banners';
      const response = await fetch(url, { method, headers: adminHeaders(), body: JSON.stringify(banner) });
      const body = await parseResponse<{ banner: Banner }>(response);
      return body.banner;
    },

    async deleteBanner(id: string): Promise<void> {
      const response = await fetch(`/api/admin/banners/${id}`, { method: 'DELETE', headers: adminHeaders() });
      await parseResponse(response);
    },

    async customers(): Promise<CustomerSummary[]> {
      const response = await fetch('/api/admin/customers', { headers: adminHeaders() });
      const body = await parseResponse<{ customers: CustomerSummary[] }>(response);
      return body.customers;
    },

    async exportCustomers(): Promise<void> {
      window.open(`/api/admin/customers/export`, '_blank');
    },

    async report(from: string, to: string): Promise<ReportSummary> {
      const response = await fetch(`/api/admin/reports?from=${from}&to=${to}`, { headers: adminHeaders() });
      return parseResponse(response);
    },

    async settings(): Promise<{ settings: StoreSettings; sender: StoreSender; features: { mercadoPagoConfigured: boolean; starkenConfigured: boolean; emailConfigured: boolean } }> {
      const response = await fetch('/api/admin/settings', { headers: adminHeaders() });
      return parseResponse(response);
    },

    async updateSettings(payload: { settings?: Partial<StoreSettings>; sender?: Partial<StoreSender> }): Promise<{ settings: StoreSettings; sender: StoreSender }> {
      const response = await fetch('/api/admin/settings', { method: 'PUT', headers: adminHeaders(), body: JSON.stringify(payload) });
      return parseResponse(response);
    },

    async newsletter(): Promise<Array<{ email: string; status: string; source: string; created_at: string }>> {
      const response = await fetch('/api/admin/newsletter', { headers: adminHeaders() });
      const body = await parseResponse<{ subscribers: Array<{ email: string; status: string; source: string; created_at: string }> }>(response);
      return body.subscribers;
    },

    async exportNewsletter(): Promise<void> {
      window.open(`/api/admin/newsletter/export`, '_blank');
    },

    async giftcards(): Promise<Giftcard[]> {
      const response = await fetch('/api/admin/giftcards', { headers: adminHeaders() });
      const body = await parseResponse<{ giftcards: Giftcard[] }>(response);
      return body.giftcards;
    },
    async createGiftcard(payload: { amount: number; recipientName?: string; recipientEmail?: string; senderName?: string; message?: string; expiresAt?: string | null; sendEmail?: boolean }): Promise<Giftcard> {
      const response = await fetch('/api/admin/giftcards', { method: 'POST', headers: adminHeaders(), body: JSON.stringify(payload) });
      const body = await parseResponse<{ giftcard: Giftcard }>(response);
      return body.giftcard;
    },
    async cancelGiftcard(id: string): Promise<Giftcard> {
      const response = await fetch(`/api/admin/giftcards/${id}`, { method: 'PATCH', headers: adminHeaders(), body: JSON.stringify({ status: 'cancelled' }) });
      const body = await parseResponse<{ giftcard: Giftcard }>(response);
      return body.giftcard;
    },
    async resendGiftcard(id: string): Promise<void> {
      const response = await fetch(`/api/admin/giftcards/${id}/resend`, { method: 'POST', headers: adminHeaders() });
      await parseResponse(response);
    },
    async downloadGiftcardImage(id: string, code: string): Promise<void> {
      const response = await fetch(`/api/admin/giftcards/${id}/image`, { headers: { authorization: `Bearer ${window.localStorage.getItem('runmore_admin_token') || ''}` } });
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `giftcard-${code}.svg`;
      a.click();
      URL.revokeObjectURL(url);
    },

    async expenses(from?: string, to?: string): Promise<Expense[]> {
      const qs = from && to ? `?from=${from}&to=${to}` : '';
      const response = await fetch(`/api/admin/expenses${qs}`, { headers: adminHeaders() });
      const body = await parseResponse<{ expenses: Expense[] }>(response);
      return body.expenses;
    },
    async saveExpense(exp: Partial<Expense>): Promise<Expense> {
      const method = exp.id ? 'PUT' : 'POST';
      const url = exp.id ? `/api/admin/expenses/${exp.id}` : '/api/admin/expenses';
      const response = await fetch(url, { method, headers: adminHeaders(), body: JSON.stringify(exp) });
      const body = await parseResponse<{ expense: Expense }>(response);
      return body.expense;
    },
    async deleteExpense(id: string): Promise<void> {
      const response = await fetch(`/api/admin/expenses/${id}`, { method: 'DELETE', headers: adminHeaders() });
      await parseResponse(response);
    },

    async finance(from: string, to: string): Promise<FinanceSummary> {
      const response = await fetch(`/api/admin/finance?from=${from}&to=${to}`, { headers: adminHeaders() });
      return parseResponse(response);
    },

    async createManualOrder(payload: ManualOrderPayload): Promise<{ orderId: string; orderNumber: number }> {
      const response = await fetch('/api/admin/manual-orders', { method: 'POST', headers: adminHeaders(), body: JSON.stringify(payload) });
      return parseResponse(response);
    }
  }
};
